import { supabase } from '@/lib/supabase'

export interface BotStatistics {
  total_predictions: number
  correct_predictions: number
  skip_predictions: number
  loss_predictions: number
  win_predictions: number
  accuracy_rate: number
  current_prediction?: string
  sequence_length?: number
  win_probability?: number
  loss_probability?: number
  last_prediction_at?: string
  analyzed_sequence?: string
}

export const calculateBotStatistics = async (botId: string): Promise<BotStatistics> => {
  const retryWithBackoff = async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error: any) {
        console.warn(`Attempt ${attempt}/${maxRetries} failed:`, error?.message);
        
        if (attempt === maxRetries) throw error;
        
        // Exponential backoff
        const delay = baseDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    throw new Error('Max retries exceeded');
  };

  try {
    console.debug('Calculating bot statistics for bot:', botId);
    
    // Get all trades for this bot with retry logic
    const { data: trades, error: tradesError } = await retryWithBackoff(async () => {
      return await supabase
        .from('bot_realtime_trades')
        .select('predicted_result, win')
        .eq('bot_id', botId);
    });

    if (tradesError) {
      console.error('Error fetching trades after retries:', tradesError);
      // Don't throw, continue with empty trades for graceful degradation
    }

    // Get current prediction data using maybeSingle to handle no data case
    const { data: currentPrediction, error: predictionError } = await retryWithBackoff(async () => {
      return await supabase
        .from('bot_current_prediction')
        .select('*')
        .eq('bot_id', botId)
        .maybeSingle();
    });

    if (predictionError) {
      console.error('Error fetching current prediction after retries:', {
        error: predictionError,
        code: predictionError.code,
        message: predictionError.message,
        botId
      });
      // Continue with null prediction for graceful degradation
    }

    // Calculate statistics
    const totalPredictions = trades?.length || 0
    const skipPredictions = trades?.filter(t => t.predicted_result === 'SKIP').length || 0
    const lossPredictions = trades?.filter(t => t.predicted_result === 'LOSS').length || 0
    const winPredictions = trades?.filter(t => t.predicted_result === 'WIN').length || 0
    
    // Calculate correct predictions (only for WIN/LOSS, skip SKIP predictions)
    const nonSkipTrades = trades?.filter(t => t.predicted_result !== 'SKIP') || []
    const correctPredictions = nonSkipTrades.filter(trade => {
      const predictedWin = trade.predicted_result === 'WIN'
      const actualWin = trade.win === 1
      return (predictedWin && actualWin) || (!predictedWin && !actualWin)
    }).length

    // Calculate accuracy rate (exclude SKIP predictions)
    const accuracyRate = nonSkipTrades.length > 0 
      ? (correctPredictions / nonSkipTrades.length) * 100 
      : 0

    return {
      total_predictions: totalPredictions,
      correct_predictions: correctPredictions,
      skip_predictions: skipPredictions,
      loss_predictions: lossPredictions,
      win_predictions: winPredictions,
      accuracy_rate: accuracyRate,
      current_prediction: currentPrediction?.current_prediction,
      sequence_length: currentPrediction?.sequence_length,
      win_probability: currentPrediction?.win_probability,
      loss_probability: currentPrediction?.loss_probability,
      last_prediction_at: currentPrediction?.last_prediction_at,
      analyzed_sequence: currentPrediction?.analyzed_sequence
    }
  } catch (error) {
    console.error('Error calculating bot statistics:', error)
    return {
      total_predictions: 0,
      correct_predictions: 0,
      skip_predictions: 0,
      loss_predictions: 0,
      win_predictions: 0,
      accuracy_rate: 0
    }
  }
}